// templateInputs.ts
// The wizard's input state: built from a template parse (or a staged block),
// turned into merge inputs, and checked (required fields, names in use).

import { QuestionnaireNames } from "utils/questionnaireNames"
import {
  StagedBlock,
  TemplateCatalogueItem,
  TemplateInput,
  TemplateInputValue,
  TemplateParseResult,
  TemplateRef,
} from "types/template.types"

/** What the user has filled in for one input. */
export interface InputState {
  /** TEXT, LIST (one item per line), NAME, a typed PIPE, typed LISTREF items */
  text: string
  /** PIPE and LISTREF: typed in, or picked from the questionnaire */
  mode: "type" | "pick"
  /** The picked earlier answer (PIPE) or list (LISTREF) */
  pick: string
  /** OPTION */
  on: boolean
  /** The ticked exceptions (inputs that allow exceptions) */
  ticked: string[]
}

/** The input states, by input label. */
export type InputStates = Record<string, InputState>

/** Splits a value into its lines (CRLF or LF). */
const linesOf = (value: string): string[] =>
  value === "" ? [] : value.split(/\r?\n/)

/** The non-empty lines of a value. */
const itemsOf = (value: string): string[] =>
  linesOf(value).filter((line) => line.trim() !== "")

/** The items of every list, each once, in first-seen order. */
const union = (...lists: string[][]): string[] => {
  const out: string[] = []
  for (const list of lists)
    for (const item of list) if (!out.includes(item)) out.push(item)
  return out
}

/** Every input of a parsed template, in section order. */
export const allInputs = (parse: TemplateParseResult): TemplateInput[] =>
  parse.sections.flatMap((section) => section.inputs)

/** An exception as it is compared: without its properties (`[[open:1]]`), trimmed. */
const exceptionKey = (exc: string): string =>
  exc.replace(/\[\[.*?\]\]/g, "").trim()

/** The template's own exceptions (`SETTING EXCEPTIONS`). */
export const templateExceptions = (parse: TemplateParseResult): string[] =>
  parse.information.exceptions.filter(
    (exc): exc is string => typeof exc === "string"
  )

/** True when the input offers exception checkboxes. */
export const allowsExceptions = (input: TemplateInput): boolean =>
  input.type === "EXCEPTIONS" ||
  ((input.type === "LIST" || input.type === "LISTREF") && input.exceptions)

/**
 * The exceptions an input offers: those already in the questionnaire, the
 * template's own, an EXCEPTIONS input's defaults, and anything ticked.
 */
export const exceptionChoices = (
  input: TemplateInput,
  parse: TemplateParseResult,
  names: QuestionnaireNames,
  ticked: string[]
): string[] =>
  union(
    names.exceptions,
    templateExceptions(parse),
    input.type === "EXCEPTIONS" ? itemsOf(input.value) : [],
    ticked
  )

/** The state of an input from the template's defaults. */
const defaultState = (
  input: TemplateInput,
  parse: TemplateParseResult,
  names: QuestionnaireNames
): InputState => {
  const state: InputState = {
    text: input.type === "EXCEPTIONS" || input.type === "OPTION" ? "" : input.value,
    mode: "type",
    pick: "",
    on: input.type === "OPTION" && input.value.toLowerCase() === "on",
    ticked: [],
  }
  if (input.type === "EXCEPTIONS") {
    state.ticked = union(templateExceptions(parse), itemsOf(input.value))
  } else if (allowsExceptions(input)) {
    // A list's default already holds its own exceptions (`...//Prefer not to
    // say`): those lines start ticked, the rest are the typed items, and the
    // other exceptions are offered unticked. A line is matched without
    // properties (`Other` is `Other [[open:1]]`) and ticks the known form.
    const known = union(templateExceptions(parse), names.exceptions)
    const lines = linesOf(input.value)
    const knownOf = (line: string) =>
      known.find((exc) => exceptionKey(exc) === exceptionKey(line))
    state.ticked = union(
      lines.map(knownOf).filter((exc): exc is string => exc !== undefined)
    )
    state.text = lines.filter((line) => knownOf(line) === undefined).join("\n")
  }
  return state
}

/** The state of an input from a staged block's value (editing it again). */
const restoredState = (
  input: TemplateInput,
  value: TemplateInputValue,
  blockExceptions: string[]
): InputState => {
  const state: InputState = { text: "", mode: "type", pick: "", on: false, ticked: [] }
  switch (input.type) {
    case "OPTION":
      state.on = value.value === "on"
      return state
    case "EXCEPTIONS":
      state.ticked = itemsOf(value.value)
      return state
    case "PIPE":
      if (value.kind === "pipe") {
        state.mode = "pick"
        state.pick = value.value
      } else state.text = value.value
      return state
    case "LIST":
    case "LISTREF": {
      // The ticked exceptions were added as extra lines: take them back out.
      const lines = linesOf(value.value)
      const isExc = (line: string) =>
        allowsExceptions(input) && blockExceptions.includes(line)
      state.ticked = lines.filter(isExc)
      if (value.ref) {
        state.mode = "pick"
        state.pick = value.ref
      } else state.text = lines.filter((line) => !isExc(line)).join("\n")
      return state
    }
    default:
      state.text = value.value
      return state
  }
}

/**
 * The starting state of every input: the template's defaults, or the values
 * of the staged block being edited.
 *
 * @param parse - The parsed template
 * @param names - The questionnaire's names (its exceptions count as exceptions in a list default)
 * @param initial - The staged block being edited, if any
 */
export const initialStates = (
  parse: TemplateParseResult,
  names: QuestionnaireNames,
  initial?: StagedBlock
): InputStates => {
  const states: InputStates = {}
  for (const input of allInputs(parse)) {
    const saved = initial?.inputs.find((v) => v.label === input.label)
    states[input.label] = saved
      ? restoredState(input, saved, initial?.exceptions ?? [])
      : defaultState(input, parse, names)
  }
  return states
}

/** The merge input of one input. Values are strings, NAMEs trimmed. */
const inputValue = (
  input: TemplateInput,
  state: InputState
): TemplateInputValue => {
  const { label } = input
  const withTicked = (lines: string[]) =>
    [...lines, ...(allowsExceptions(input) ? state.ticked : [])].join("\n")
  switch (input.type) {
    case "OPTION":
      return { label, value: state.on ? "on" : "off" }
    case "EXCEPTIONS":
      return { label, value: state.ticked.join("\n") }
    case "NAME":
      return { label, value: state.text.trim() }
    case "PIPE":
      return state.mode === "pick"
        ? { label, value: state.pick, kind: "pipe" }
        : { label, value: state.text, kind: "text" }
    case "LISTREF":
      return state.mode === "pick"
        ? { label, value: withTicked([]), ref: state.pick }
        : { label, value: withTicked(linesOf(state.text)) }
    case "LIST":
      return { label, value: withTicked(linesOf(state.text)) }
    default:
      return { label, value: state.text }
  }
}

/** The merge inputs of every input, in template order. */
export const inputValues = (
  parse: TemplateParseResult,
  states: InputStates
): TemplateInputValue[] =>
  allInputs(parse).map((input) => inputValue(input, states[input.label]))

/**
 * The request's exceptions: every ticked exception, each once, in order.
 * A template without inputs that allow exceptions keeps its own (the server
 * takes the array as leading, so an empty one would drop them).
 */
export const tickedExceptions = (
  parse: TemplateParseResult,
  states: InputStates
): string[] => {
  const inputs = allInputs(parse).filter(allowsExceptions)
  if (inputs.length === 0) return templateExceptions(parse)
  return union(...inputs.map((input) => states[input.label].ticked))
}

/** True when a required input has nothing in it. OPTION is never required. */
export const isMissing = (input: TemplateInput, state: InputState): boolean => {
  if (input.optional || input.type === "OPTION") return false
  switch (input.type) {
    case "EXCEPTIONS":
      return state.ticked.length === 0
    case "PIPE":
      return state.mode === "pick" ? state.pick === "" : state.text.trim() === ""
    case "LISTREF":
      return state.mode === "pick"
        ? state.pick === ""
        : itemsOf(state.text).length === 0
    case "LIST":
      return itemsOf(state.text).length === 0
    default:
      return state.text.trim() === ""
  }
}

/** The element keywords that define a name, and whether a ":" must follow it. */
const DEFINING: { keyword: string; colon: boolean }[] = [
  { keyword: "Q ", colon: true },
  { keyword: "HIDDEN ", colon: false },
  { keyword: "DATA ", colon: false },
  { keyword: "TEXT ", colon: true },
  { keyword: "RANGE LIST ", colon: true },
  { keyword: "LIST ", colon: false },
]

/**
 * True when a merged block text defines a name: a line (trimmed) `Q <name>:`,
 * `HIDDEN <name>`, `DATA <name>`, `TEXT <name>:`, `LIST <name>` or
 * `RANGE LIST <name>:`. A name only referred to (`<<name>>`, `FROM name`)
 * is not defined. The same rule as the server's tray check.
 *
 * @param text - The merged text (the wizard's preview)
 * @param name - The trimmed name
 */
export const definesName = (text: string, name: string): boolean =>
  text.split(/\r?\n/).some((raw) => {
    const line = raw.trim()
    const match = DEFINING.find((d) => line.startsWith(d.keyword))
    if (!match) return false
    const rest = line.slice(match.keyword.length)
    const colon = rest.indexOf(":")
    if (match.colon && colon < 0) return false
    return (colon < 0 ? rest : rest.slice(0, colon)).trim() === name
  })

/**
 * The inline warning of a NAME input, or null. Only a name the merged text
 * defines is checked (a NAME only referred to, or inside a part an OPTION
 * dropped, is not).
 *
 * @param name - The input's value
 * @param names - The questionnaire's names
 * @param stagedNames - The NAME values of the blocks already in the tray
 * @param merged - The wizard's current preview text
 */
export const nameWarning = (
  name: string,
  names: QuestionnaireNames,
  stagedNames: string[],
  merged: string
): string | null => {
  const value = name.trim()
  if (value === "" || !definesName(merged, value)) return null
  const used = [
    ...names.questions,
    ...names.hidden,
    ...names.data,
    ...names.lists,
    ...names.rangeLists,
  ]
  if (used.includes(value)) return "Already used in this questionnaire"
  if (stagedNames.includes(value)) return "Already used by a staged block"
  return null
}

/** True when two template references point at the same template. */
export const sameRef = (a: TemplateRef, b: TemplateRef): boolean =>
  a.file === b.file && a.access === b.access && a.template === b.template

/**
 * The NAME values of staged blocks, read through the catalogue (a staged
 * block's inputs carry no kind).
 *
 * @param blocks - The staged blocks to read
 * @param items - The catalogue
 */
export const stagedBlockNames = (
  blocks: StagedBlock[],
  items: TemplateCatalogueItem[]
): string[] =>
  union(
    ...blocks.map((block) => {
      const item = items.find((it) => sameRef(it, block.ref))
      const nameLabels = (item?.inputs ?? [])
        .filter((input) => input.type === "NAME")
        .map((input) => input.label)
      return block.inputs
        .filter((v) => nameLabels.includes(v.label) && v.value.trim() !== "")
        .map((v) => v.value.trim())
    })
  )

/**
 * The first NAME value of a staged block (its first question's name), for
 * the tray row; empty when the template has no NAME input or is not in the
 * catalogue.
 *
 * @param block - The staged block
 * @param items - The catalogue
 */
export const firstNameValue = (
  block: StagedBlock,
  items: TemplateCatalogueItem[]
): string => {
  const item = items.find((it) => sameRef(it, block.ref))
  for (const input of item?.inputs ?? []) {
    if (input.type !== "NAME") continue
    const value = block.inputs.find((v) => v.label === input.label)?.value.trim()
    if (value) return value
  }
  return ""
}

/**
 * The texts to highlight in the preview: the current values (list items one
 * by one), longest first so a longer value wins over a part of it.
 */
export const highlightTerms = (values: TemplateInputValue[]): string[] => {
  const terms: string[] = []
  for (const v of values) {
    if (v.ref) terms.push(v.ref)
    for (const line of itemsOf(v.value)) {
      if (line === "on" || line === "off") continue
      terms.push(line.trim())
    }
  }
  return union(terms.filter((t) => t !== "")).sort((a, b) => b.length - a.length)
}

/** Escapes a text for use inside a regular expression. */
const escapeRegExp = (text: string): string =>
  text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/** A letter, digit or underscore (as a regular expression class). */
const WORD_CHAR = "[\\p{L}\\p{N}_]"

/**
 * Splits a text into plain parts and parts that match a term. A term that
 * starts or ends with a letter or digit only matches as a whole word, so a
 * value "8" does not mark the 8 inside "18".
 *
 * @param text - The text to split
 * @param terms - The texts to mark, longest first
 * @returns The parts in order; `mark` is true for a matched term
 */
export const highlightParts = (
  text: string,
  terms: string[]
): { text: string; mark: boolean }[] => {
  if (text === "") return []
  if (terms.length === 0) return [{ text, mark: false }]
  const word = new RegExp(WORD_CHAR, "u")
  const pattern = terms
    .map((term) => {
      const before = word.test(term.charAt(0)) ? `(?<!${WORD_CHAR})` : ""
      const after = word.test(term.charAt(term.length - 1))
        ? `(?!${WORD_CHAR})`
        : ""
      return `${before}${escapeRegExp(term)}${after}`
    })
    .join("|")
  const re = new RegExp(pattern, "gu")
  const parts: { text: string; mark: boolean }[] = []
  let last = 0
  for (const m of text.matchAll(re)) {
    const at = m.index ?? 0
    if (at > last) parts.push({ text: text.slice(last, at), mark: false })
    parts.push({ text: m[0], mark: true })
    last = at + m[0].length
  }
  if (last < text.length) parts.push({ text: text.slice(last), mark: false })
  return parts
}
